// Builds the per-route meta() return value. Defaults flow from
// `openGraphData.js`'s `defaultData` so prerendered routes and the
// post-build OG injector (scripts/generate-og-pages.mjs) stay in sync.
//
// React Router 7 child meta() returns *replace* the parent's array
// rather than merging, so every prerendered route needs a complete set
// of tags (theme-color, og:type, og:image fallback, etc.). This helper
// fills in everything from one input.
import { defaultData, PROD_BASE_URL } from "../openGraphData.js";

interface RouteMetaInput {
  title?: string;
  ogTitle?: string;
  description: string;
  /** Filename within `/og/` (e.g. "share_growth_projections.png"). */
  ogImage?: string;
}

// React Router passes `{ location, params, data, matches }` to meta();
// we only need pathname to build an absolute og:url.
interface MetaArgsLike {
  location?: { pathname?: string };
}

export function buildMeta(
  { title, ogTitle, description, ogImage }: RouteMetaInput,
  args?: MetaArgsLike,
) {
  const resolvedTitle = title ?? defaultData.title;
  const resolvedOgTitle = ogTitle ?? title ?? defaultData.ogTitle;
  const resolvedOgImage = ogImage ?? defaultData.ogImage;
  const rawPathname = args?.location?.pathname ?? "/";
  // Strip a trailing slash (except root) so the canonical URL is stable
  // whether the route is reached as /explore/treemap or /explore/treemap/.
  // Otherwise server (prerendered, no slash) and client (slash) disagree and
  // React logs a hydration mismatch on the og:url meta tag.
  const pathname = rawPathname.length > 1 ? rawPathname.replace(/\/+$/, "") : rawPathname;
  const ogUrl = `${PROD_BASE_URL}${pathname}`;
  return [
    { title: resolvedTitle },
    { name: "theme-color", content: "#000000" },
    { name: "description", content: description },
    { property: "og:title", content: resolvedOgTitle },
    { property: "og:type", content: "website" },
    { property: "og:description", content: description },
    { property: "og:url", content: ogUrl },
    { property: "og:image", content: `${PROD_BASE_URL}/og/${resolvedOgImage}` },
  ];
}
